import { shouldPolyfill as shouldPolyfillDurationFormat } from "@formatjs/intl-durationformat/should-polyfill.js"
import { shouldPolyfill as shouldPolyfillListFormat } from "@formatjs/intl-listformat/should-polyfill.js"
import { shouldPolyfill as shouldPolyfillNumberFormat } from "@formatjs/intl-numberformat/should-polyfill.js"
import i18n from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import Backend from "i18next-http-backend"
import { initReactI18next } from "react-i18next"

import commonEn from "../locales/en/common.json"
import errorsEn from "../locales/en/errors.json"
import gatedEn from "../locales/en/gated.json"
import parkEn from "../locales/en/park.json"
import passesEn from "../locales/en/passes.json"
import receiptEn from "../locales/en/receipt.json"
import reserveEn from "../locales/en/reserve.json"
import welcomeEn from "../locales/en/welcome.json"
import commonEs from "../locales/es/common.json"
import errorsEs from "../locales/es/errors.json"
import gatedEs from "../locales/es/gated.json"
import parkEs from "../locales/es/park.json"
import passesEs from "../locales/es/passes.json"
import receiptEs from "../locales/es/receipt.json"
import reserveEs from "../locales/es/reserve.json"
import welcomeEs from "../locales/es/welcome.json"

export const defaultNS = "common"
export const resources = {
  en: {
    common: commonEn,
    errors: errorsEn,
    gated: gatedEn,
    park: parkEn,
    passes: passesEn,
    receipt: receiptEn,
    reserve: reserveEn,
    welcome: welcomeEn,
  },
  es: {
    common: commonEs,
    errors: errorsEs,
    gated: gatedEs,
    park: parkEs,
    passes: passesEs,
    receipt: receiptEs,
    reserve: reserveEs,
    welcome: welcomeEs,
  },
} as const

// Static import maps keyed by supported locale — avoids bare-specifier dynamic
// imports (e.g. `@formatjs/…/${locale}.js`) that Safari rejects at runtime
// because it only accepts "/" / "./" / "../" prefixes in native ESM.
// @vite-ignore was the previous workaround, but that tells Vite *not* to
// bundle the import, which causes the same Safari failure in production.
const LIST_FORMAT_LOCALE_DATA: Record<string, () => Promise<unknown>> = {
  en: () => import("@formatjs/intl-listformat/locale-data/en.js"),
  es: () => import("@formatjs/intl-listformat/locale-data/es.js"),
}

const NUMBER_FORMAT_LOCALE_DATA: Record<string, () => Promise<unknown>> = {
  en: () => import("@formatjs/intl-numberformat/locale-data/en.js"),
  es: () => import("@formatjs/intl-numberformat/locale-data/es.js"),
}

async function loadListFormatPolyfill(locale: string) {
  if (shouldPolyfillListFormat(locale)) {
    const baseLocale = locale.split("-")[0]
    console.log("Load: intl-listformat pollyfill")
    await import("@formatjs/intl-listformat/polyfill-force.js")
    await (
      LIST_FORMAT_LOCALE_DATA[baseLocale] || LIST_FORMAT_LOCALE_DATA["en"]
    )()
    console.log("Finished Loading: intl-listformat pollyfill")
  }
}

function hasNumberFormatUnitSupport() {
  try {
    new Intl.NumberFormat("en", { style: "unit", unit: "hour" })
    return true
  } catch {
    return false
  }
}

// Uses polyfill-force when the locale is unsupported or when
// style="unit" throws (iOS 14).
async function loadNumberFormatPolyfill(locale: string) {
  if (shouldPolyfillNumberFormat(locale) || !hasNumberFormatUnitSupport()) {
    const baseLocale = locale.split("-")[0]
    console.log("Load: intl-numberformat pollyfill")
    await import("@formatjs/intl-numberformat/polyfill-force.js")
    await (
      NUMBER_FORMAT_LOCALE_DATA[baseLocale] || NUMBER_FORMAT_LOCALE_DATA["en"]
    )()
    console.log("Finished Loading: intl-numberformat pollyfill")
  }
}

async function ensureDurationFormatPolyfill() {
  if (shouldPolyfillDurationFormat()) {
    console.log("Load: intl-durationformat pollyfill")
    await import("@formatjs/intl-durationformat/polyfill-force.js")
    console.log("Finished Loading: intl-durationformat pollyfill")
  }
}

async function loadAllPolyfills(locale: string) {
  const resolvedLocale = locale || "en"
  // Order matters
  await loadListFormatPolyfill(resolvedLocale)
  await loadNumberFormatPolyfill(resolvedLocale)
  await ensureDurationFormatPolyfill()
}

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    debug: true,
    interpolation: {
      escapeValue: false,
    },
    supportedLngs: ["en", "es"],
    nonExplicitSupportedLngs: true,
    ns: [
      "common",
      "errors",
      "gated",
      "park",
      "passes",
      "receipt",
      "reserve",
      "welcome",
    ],
    react: {
      useSuspense: true,
    },
    defaultNS: defaultNS,
    resources,
  })
  // Polyfills load after init via .then() rather than the "initialized" event
  // to avoid a race where the event fires before the listener is registered.
  .then(async () => {
    return await loadAllPolyfills(i18n.language)
  })

i18n.on("languageChanged", async (lng) => {
  await loadAllPolyfills(lng)
  document.documentElement.lang = lng
})

export default i18n
